import React, { Fragment, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Table from './Table';
import FormModal from './FormModal';
import DeleteModal from './DeleteModal';
import GenerateModal from './GenerateModal';
import './AdminApparelItemParts.scss';
import { Helmet } from 'react-helmet';
import Layout from './../Layout';
import * as actions from './AdminApparelItemParts.actions';
import * as selectors from './AdminApparelItemParts.selectors';
import { stringifyEqualityCheck } from './../../utils';
import * as dialog from './../common/dialog';
import * as loginSelectors from './../Login/Login.selectors';
import { Link } from 'react-router-dom';

const AdminApparelItemParts = (props) => {
    /* State to props */
    const apparelItemParts = useSelector(selectors.getApparelItemParts, stringifyEqualityCheck);
    const fetching = useSelector(selectors.getFetching);
    const saving = useSelector(selectors.getSaving);
    const isDeleting = useSelector(selectors.getIsDeleting);
    const idModal = useSelector(selectors.getIdModal);
    const pagination = useSelector(selectors.getPagination, stringifyEqualityCheck);
    const apparelItemPart = useSelector(selectors.getApparelItemPart, stringifyEqualityCheck);
    const sort = useSelector(selectors.getSort, stringifyEqualityCheck);
    const apparelItem = useSelector(selectors.getApparelItem, stringifyEqualityCheck);
    const selectedSizes = useSelector(selectors.getSelectedSizes, stringifyEqualityCheck);
    const selectedLogos = useSelector(selectors.getSelectedLogos, stringifyEqualityCheck);
    const isGenerating = useSelector(selectors.getIsGenerating);
    const user = useSelector(loginSelectors.getUser, stringifyEqualityCheck);

    /* Dispatch to props */
    const dispatch = useDispatch();
    const fetchApparelItemParts = useCallback((id) => dispatch(actions.fetchApparelItemParts(id)), [dispatch]);
    const setIdModal = useCallback((size, logo, sku) => dispatch(actions.setIdModal(size, logo, sku)), [dispatch]);
    const setActivePage = useCallback((value) => dispatch(actions.setActivePage(value)), [dispatch]);
    const resetState = useCallback(() => dispatch(actions.resetState()), [dispatch]);
    const setApparelItemPart = useCallback((id) => dispatch(actions.setApparelItemPart(id)), [dispatch]);
    const setApparelItemPartField = useCallback((field, value) => dispatch(actions.setApparelItemPartField(field, value)), [dispatch]);
    const resetApparelItemPart = useCallback(() => dispatch(actions.resetApparelItemPart()), [dispatch]);
    const saveApparelItemPart = useCallback(() => dispatch(actions.saveApparelItemPart()), [dispatch]);
    const deleteApparelItemPart = useCallback(() => dispatch(actions.deleteApparelItemPart()), [dispatch]);
    const setSort = useCallback((column, type) => dispatch(actions.setSort(column, type)), [dispatch]);
    const fetchApparelItem = useCallback((id) => dispatch(actions.fetchApparelItem(id)), [dispatch]);
    const setItemId = useCallback((value) => dispatch(actions.setItemId(value)), [dispatch]);
    const generateParts = useCallback(() => dispatch(actions.generateParts()), [dispatch]);
    const checkSize = useCallback((size, value) => dispatch(actions.checkSize(size, value)), [dispatch]);
    const checkLogo = useCallback((logo, value) => dispatch(actions.checkLogo(logo, value)), [dispatch]);
    const showFormModal = useCallback(() => dispatch(dialog.actions.showDialog('form-modal')), [dispatch]);
    const hideFormModal = useCallback(() => dispatch(dialog.actions.hideDialog('form-modal')), [dispatch]);
    const showDeleteModal = useCallback(() => dispatch(dialog.actions.showDialog('delete-modal')), [dispatch]);
    const hideDeleteModal = useCallback(() => dispatch(dialog.actions.hideDialog('delete-modal')), [dispatch]);
    const showGenerateModal = useCallback(() => dispatch(dialog.actions.showDialog('generate-modal')), [dispatch]);
    const hideGenerateModal = useCallback(() => dispatch(dialog.actions.hideDialog('generate-modal')), [dispatch]);

    /* componentDidMount */
    useEffect(() => {
        fetchApparelItem(props.match.params.id);
        fetchApparelItemParts(props.match.params.id);
        setItemId(props.match.params.id);
        return () => {
            hideFormModal();
            resetState();
        };
    }, [fetchApparelItem, fetchApparelItemParts, resetState, hideFormModal, setItemId, props.match.params.id]);

    let permissions = [];
    if (user && user.uth_permissions != null) {
        permissions = JSON.parse(user.uth_permissions);
    }

    let itemName = null;
    if (apparelItem != null) {
        itemName = apparelItem.name;
    }

    let generateButton = null;
    let createButton = null;
    if (permissions.includes('apparel-parts-create')) {
        generateButton = <div className="generate-button" onClick={showGenerateModal}><i className="fas fa-folder-plus" /> Generate Parts</div>;
        createButton = <div className="create-button" onClick={showFormModal}><i className="fas fa-folder-plus" /> Add Part</div>;
    }

    return (
        <Layout
            title={<Fragment><i className="fas fa-file-alt"></i> Apparel Item Parts</Fragment>}
            history={props.history}
        >
            <Helmet><title>Apparel Item Parts</title></Helmet>
            <div className="admin-apparel-item-parts-container">
                <div className="breadcrumbs">/ <Link to="/admin/apparel-items">Apparel Items</Link> / {itemName}</div>
                <h2>Apparel Item Parts <span>({itemName})</span></h2>
                <div className="action-buttons">
                    {generateButton}
                    {createButton}
                </div>
                <Table
                    apparelItemParts={apparelItemParts}
                    fetching={fetching}
                    user={user}
                    pagination={pagination}
                    sort={sort}
                    showFormModal={showFormModal}
                    setIdModal={setIdModal}
                    setActivePage={setActivePage}
                    setApparelItemPart={setApparelItemPart}
                    showDeleteModal={showDeleteModal}
                    setSort={setSort}
                />
            </div>
            <FormModal
                apparelItemPart={apparelItemPart}
                saving={saving}
                idModal={idModal}
                hideFormModal={hideFormModal}
                setIdModal={setIdModal}
                setApparelItemPartField={setApparelItemPartField}
                setApparelItemPart={setApparelItemPart}
                resetApparelItemPart={resetApparelItemPart}
                saveApparelItemPart={saveApparelItemPart}
            />
            <DeleteModal
                idModal={idModal}
                isDeleting={isDeleting}
                deleteApparelItemPart={deleteApparelItemPart}
                hideDeleteModal={hideDeleteModal}
                setIdModal={setIdModal}
            />
            <GenerateModal
                idModal={idModal}
                isGenerating={isGenerating}
                selectedSizes={selectedSizes}
                selectedLogos={selectedLogos}
                apparelItemPart={apparelItemPart}
                checkSize={checkSize}
                checkLogo={checkLogo}
                generateParts={generateParts}
                hideGenerateModal={hideGenerateModal}
                setIdModal={setIdModal}
                resetApparelItemPart={resetApparelItemPart}
                setApparelItemPartField={setApparelItemPartField}
            />
        </Layout>
    );
};

export default AdminApparelItemParts;
